import _ from "lodash";

export default {
    data() {
        return {
            stateWorkflowsByProject: {},
            loadingWorkflows: {}
        };
    },
    methods: {
        async ensureWorkflowsAsync(projectIds) {
            const ids = Array.isArray(projectIds)
                ? projectIds.filter(id => id && typeof id === "string" && id.trim() !== "")
                : (projectIds && typeof projectIds === "string" && projectIds.trim() !== "" ? [projectIds.trim()] : []);

            const uniqueIds = _.uniq(ids);
            if (uniqueIds.length === 0) {
                uniqueIds.push("default");
            }

            const fetchPromises = [];
            for (const pid of uniqueIds) {
                const cacheKey = pid || "default";
                if (this.stateWorkflowsByProject[cacheKey] || this.loadingWorkflows[cacheKey]) {
                    continue;
                }

                this.$set(this.loadingWorkflows, cacheKey, true);
                const params = pid && pid !== "default" ? { projectId: pid } : {};
                const p = this.$http
                    .get("/TimeManagement/GetStateWorkflow", { params })
                    .then(res => {
                        if (res && res.data) {
                            this.$set(this.stateWorkflowsByProject, cacheKey, res.data);
                            if (res.data.projectId && res.data.projectId !== cacheKey) {
                                this.$set(this.stateWorkflowsByProject, res.data.projectId, res.data);
                            }
                            if (res.data.projectName && res.data.projectName !== cacheKey) {
                                this.$set(this.stateWorkflowsByProject, res.data.projectName, res.data);
                            }
                        }
                    })
                    .catch(err => {
                        console.warn(`[mixinWorkItemState] Failed to fetch state workflow for project ${pid}:`, err);
                    })
                    .finally(() => {
                        this.$set(this.loadingWorkflows, cacheKey, false);
                    });

                fetchPromises.push(p);
            }

            if (fetchPromises.length > 0) {
                await Promise.all(fetchPromises);
            }
        },

        getWorkflowForProject(projectId) {
            if (!projectId) {
                return this.stateWorkflowsByProject["default"] || Object.values(this.stateWorkflowsByProject)[0] || null;
            }
            const key = projectId.trim();
            if (this.stateWorkflowsByProject[key]) {
                return this.stateWorkflowsByProject[key];
            }
            // Match case-insensitively or by projectId / projectName
            const norm = key.toLowerCase();
            for (const [k, v] of Object.entries(this.stateWorkflowsByProject)) {
                if (k.toLowerCase() === norm) return v;
                if (v && v.projectId && v.projectId.toLowerCase() === norm) return v;
                if (v && v.projectName && v.projectName.toLowerCase() === norm) return v;
            }
            return null;
        },

        getTypeWorkflow(projectId, workItemType) {
            const wf = this.getWorkflowForProject(projectId);
            if (!wf || !wf.types) {
                return null;
            }

            const targetType = (workItemType || "Task").trim();
            if (wf.types[targetType]) {
                return wf.types[targetType];
            }

            // Case-insensitive lookup
            const norm = targetType.toLowerCase();
            for (const [k, v] of Object.entries(wf.types)) {
                if (k.toLowerCase() === norm) {
                    return v;
                }
            }

            // Fallback to primary task type or first type
            return wf.types["Task"] || Object.values(wf.types)[0] || null;
        },

        stateActionsFor(item) {
            if (!item) return [];

            const typeWf = this.getTypeWorkflow(item.projectId || item.projectName, item.workItemType);
            const currentState = (item.state || "").trim();

            if (typeWf && typeWf.actions) {
                let actionsForState = typeWf.actions[currentState];
                if (!actionsForState) {
                    // Try case-insensitive lookup
                    const norm = currentState.toLowerCase();
                    for (const [k, v] of Object.entries(typeWf.actions)) {
                        if (k.toLowerCase() === norm) {
                            actionsForState = v;
                            break;
                        }
                    }
                }

                if (actionsForState && actionsForState.length > 0) {
                    // Return primary actions (up to 3 buttons) and ensure icons have bi prefix
                    return actionsForState.slice(0, 3).map(a => ({
                        ...a,
                        icon: a.icon && !a.icon.includes("bi ") ? "bi " + a.icon : (a.icon || "bi bi-check")
                    }));
                }
            }

            // Process-aware static fallback
            const proj = ((item.projectName || item.projectId || "") + "").toLowerCase();
            const norm = currentState.toLowerCase();
            const isAgile = proj.includes("agile") || proj.includes("22222222-aaaa");
            const isBasic = proj.includes("basic") || proj.includes("33333333-bbbb");
            const isCmmi = proj.includes("cmmi") || proj.includes("fabrikam") || proj.includes("44444444-cccc");
            const isCustom = proj.includes("custom") || proj.includes("litware") || proj.includes("55555555-dddd");

            if (isAgile) {
                if (norm === "new" || norm === "proposed" || norm === "to do") {
                    return [
                        { key: "start", label: "Start", targetState: "Active", icon: "bi bi-play-fill", variant: "primary", dataTest: "btn-state-start" },
                        { key: "done", label: "Done", targetState: "Closed", icon: "bi bi-check", variant: "success", dataTest: "btn-state-done" }
                    ];
                } else if (norm === "active" || norm === "in progress") {
                    return [
                        { key: "todo", label: "New", targetState: "New", icon: "bi bi-card-list", variant: "secondary", dataTest: "btn-state-todo" },
                        { key: "done", label: "Done", targetState: "Closed", icon: "bi bi-check", variant: "success", dataTest: "btn-state-done" }
                    ];
                } else if (norm === "closed" || norm === "done") {
                    return [
                        { key: "restart", label: "Restart", targetState: "Active", icon: "bi bi-arrow-repeat", variant: "primary", dataTest: "btn-state-restart" },
                        { key: "todo", label: "New", targetState: "New", icon: "bi bi-card-list", variant: "secondary", dataTest: "btn-state-todo" }
                    ];
                }
            } else if (isBasic) {
                if (norm === "to do" || norm === "new" || norm === "proposed") {
                    return [
                        { key: "start", label: "Start", targetState: "Doing", icon: "bi bi-play-fill", variant: "primary", dataTest: "btn-state-start" },
                        { key: "done", label: "Done", targetState: "Done", icon: "bi bi-check", variant: "success", dataTest: "btn-state-done" }
                    ];
                } else if (norm === "doing" || norm === "active" || norm === "in progress") {
                    return [
                        { key: "todo", label: "To Do", targetState: "To Do", icon: "bi bi-card-list", variant: "secondary", dataTest: "btn-state-todo" },
                        { key: "done", label: "Done", targetState: "Done", icon: "bi bi-check", variant: "success", dataTest: "btn-state-done" }
                    ];
                } else if (norm === "done" || norm === "closed") {
                    return [
                        { key: "restart", label: "Restart", targetState: "Doing", icon: "bi bi-arrow-repeat", variant: "primary", dataTest: "btn-state-restart" },
                        { key: "todo", label: "To Do", targetState: "To Do", icon: "bi bi-card-list", variant: "secondary", dataTest: "btn-state-todo" }
                    ];
                }
            } else if (isCmmi) {
                if (norm === "proposed" || norm === "new" || norm === "to do") {
                    return [
                        { key: "start", label: "Start", targetState: "Active", icon: "bi bi-play-fill", variant: "primary", dataTest: "btn-state-start" },
                        { key: "done", label: "Done", targetState: "Closed", icon: "bi bi-check", variant: "success", dataTest: "btn-state-done" }
                    ];
                } else if (norm === "active" || norm === "in progress") {
                    return [
                        { key: "todo", label: "Proposed", targetState: "Proposed", icon: "bi bi-card-list", variant: "secondary", dataTest: "btn-state-todo" },
                        { key: "resolve", label: "Resolve", targetState: "Resolved", icon: "bi bi-check-circle", variant: "info", dataTest: "btn-state-resolve" },
                        { key: "done", label: "Done", targetState: "Closed", icon: "bi bi-check", variant: "success", dataTest: "btn-state-done" }
                    ];
                } else if (norm === "resolved") {
                    return [
                        { key: "restart", label: "Reopen", targetState: "Active", icon: "bi bi-arrow-repeat", variant: "primary", dataTest: "btn-state-restart" },
                        { key: "done", label: "Done", targetState: "Closed", icon: "bi bi-check", variant: "success", dataTest: "btn-state-done" }
                    ];
                } else if (norm === "closed" || norm === "done") {
                    return [
                        { key: "restart", label: "Restart", targetState: "Active", icon: "bi bi-arrow-repeat", variant: "primary", dataTest: "btn-state-restart" },
                        { key: "todo", label: "Proposed", targetState: "Proposed", icon: "bi bi-card-list", variant: "secondary", dataTest: "btn-state-todo" }
                    ];
                }
            } else if (isCustom) {
                if (norm === "to do" || norm === "new" || norm === "proposed") {
                    return [
                        { key: "start", label: "Start", targetState: "In Progress", icon: "bi bi-play-fill", variant: "primary", dataTest: "btn-state-start" },
                        { key: "done", label: "Done", targetState: "Done", icon: "bi bi-check", variant: "success", dataTest: "btn-state-done" }
                    ];
                } else if (norm === "in progress" || norm === "active") {
                    return [
                        { key: "todo", label: "To Do", targetState: "To Do", icon: "bi bi-card-list", variant: "secondary", dataTest: "btn-state-todo" },
                        { key: "resolve", label: "Ready for QA", targetState: "Ready for QA", icon: "bi bi-check-circle", variant: "info", dataTest: "btn-state-resolve" },
                        { key: "done", label: "Done", targetState: "Done", icon: "bi bi-check", variant: "success", dataTest: "btn-state-done" }
                    ];
                } else if (norm === "ready for qa" || norm === "resolved") {
                    return [
                        { key: "restart", label: "In Progress", targetState: "In Progress", icon: "bi bi-arrow-repeat", variant: "primary", dataTest: "btn-state-restart" },
                        { key: "done", label: "Done", targetState: "Done", icon: "bi bi-check", variant: "success", dataTest: "btn-state-done" }
                    ];
                } else if (norm === "done" || norm === "closed") {
                    return [
                        { key: "restart", label: "Restart", targetState: "In Progress", icon: "bi bi-arrow-repeat", variant: "primary", dataTest: "btn-state-restart" },
                        { key: "todo", label: "To Do", targetState: "To Do", icon: "bi bi-card-list", variant: "secondary", dataTest: "btn-state-todo" }
                    ];
                }
            } else {
                // Default / Scrum fallback
                if (norm === "to do" || norm === "new" || norm === "proposed") {
                    return [
                        { key: "start", label: "Start", targetState: "In Progress", icon: "bi bi-play-fill", variant: "primary", dataTest: "btn-state-start" },
                        { key: "done", label: "Done", targetState: "Done", icon: "bi bi-check", variant: "success", dataTest: "btn-state-done" }
                    ];
                } else if (norm === "in progress" || norm === "active" || norm === "doing") {
                    return [
                        { key: "todo", label: "To Do", targetState: "To Do", icon: "bi bi-card-list", variant: "secondary", dataTest: "btn-state-todo" },
                        { key: "done", label: "Done", targetState: "Done", icon: "bi bi-check", variant: "success", dataTest: "btn-state-done" }
                    ];
                } else if (norm === "done" || norm === "closed" || norm === "completed") {
                    return [
                        { key: "restart", label: "Restart", targetState: "In Progress", icon: "bi bi-arrow-repeat", variant: "primary", dataTest: "btn-state-restart" },
                        { key: "todo", label: "To Do", targetState: "To Do", icon: "bi bi-card-list", variant: "secondary", dataTest: "btn-state-todo" }
                    ];
                }
            }

            return [];
        },

        stateOverflowFor(item) {
            if (!item) return [];

            const typeWf = this.getTypeWorkflow(item.projectId, item.workItemType);
            if (!typeWf || !typeWf.states) return [];

            const currentState = (item.state || "").trim().toLowerCase();
            const primaryActions = this.stateActionsFor(item);
            const primaryTargetStates = primaryActions.map(a => (a.targetState || "").toLowerCase());

            return typeWf.states.filter(s => {
                const nameNorm = (s.name || "").toLowerCase();
                const isCurrent = nameNorm === currentState;
                const isAlreadyPrimary = primaryTargetStates.includes(nameNorm);
                const isRemoved = s.category && s.category.toLowerCase() === "removed";
                return !isCurrent && !isAlreadyPrimary && !isRemoved;
            });
        },

        stateCategoryFor(item) {
            if (!item) return "Proposed";
            const typeWf = this.getTypeWorkflow(item.projectId, item.workItemType);
            const currentState = (item.state || "").trim().toLowerCase();

            if (typeWf && typeWf.states) {
                const found = typeWf.states.find(s => (s.name || "").toLowerCase() === currentState);
                if (found && found.category) return found.category;
            }

            if (currentState === "to do" || currentState === "new" || currentState === "proposed") return "Proposed";
            if (currentState === "in progress" || currentState === "active" || currentState === "doing") return "InProgress";
            if (currentState === "resolved") return "Resolved";
            if (currentState === "done" || currentState === "closed") return "Completed";
            if (currentState === "removed") return "Removed";

            return "Proposed";
        },

        stateBadgeVariant(category) {
            switch ((category || "").toLowerCase()) {
                case "inprogress":
                    return "primary";
                case "resolved":
                    return "info";
                case "completed":
                    return "success";
                case "removed":
                    return "danger";
                case "proposed":
                default:
                    return "secondary";
            }
        },

        taskStateOptions(projectId) {
            const typeWf = this.getTypeWorkflow(projectId, "Task");
            if (typeWf && typeWf.allStates && typeWf.allStates.length > 0) {
                return [...typeWf.allStates];
            }
            return ["To Do", "In Progress", "Done", "Removed"];
        },

        isTaskRemoved(projectId, workItemType, state) {
            const typeWf = this.getTypeWorkflow(projectId, workItemType);
            const norm = (state || "").trim().toLowerCase();
            if (typeWf && typeWf.states) {
                const found = typeWf.states.find(s => (s.name || "").toLowerCase() === norm);
                if (found) {
                    return found.category && found.category.toLowerCase() === "removed";
                }
            }
            return norm === "removed";
        }
    }
};
